import React from 'react';
import { ColumnConfig } from 'components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { formatToTz } from 'utils/date-format';
import CURRENCY from 'utils/currency';
import { PAYMENTS } from 'state/payment/type';
import { NA_EMPLOYEE_TOOLTIP } from '../../constants';

interface CreateTipDetailsColumnsArgs {
  timezone: string;
  isOrgLevel: boolean;
  getStoreName: (row: PAYMENTS.ITipsResult) => string;
}

const NaWithTooltip = ({ tooltip }: { tooltip: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-sm text-gray-blue-60 border-b border-dashed border-gray-blue-40 cursor-help">
          NA
        </span>
      </TooltipTrigger>
      <TooltipContent side="left">{tooltip}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const createTipDetailsColumns = ({
  timezone,
  isOrgLevel,
  getStoreName,
}: CreateTipDetailsColumnsArgs): ColumnConfig<PAYMENTS.ITipsResult>[] => {
  const columns: ColumnConfig<PAYMENTS.ITipsResult>[] = [
    {
      key: 'transaction_date',
      label: 'Date',
      sortable: true,
      isDate: true,
      renderCell: (row) =>
        row.transaction_date
          ? formatToTz(row.transaction_date, timezone, 'MMM dd, hh:mm a')
          : '',
    },
  ];

  if (isOrgLevel) {
    columns.push({
      key: 'store_name' as keyof PAYMENTS.ITipsResult,
      label: 'Store',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{getStoreName(row)}</div>
      ),
    });
  }

  columns.push(
    {
      key: 'ticket_id',
      label: 'Ticket Id',
      renderCell: (row) => (row.ticket_id ? row.ticket_id.toUpperCase() : ''),
    },
    {
      key: 'tip_amount',
      label: 'Tip Amount',
      sortable: true,
      renderCell: (row) => (
        <div>{CURRENCY.convertToMainUnit(row.tip_amount || 0)}</div>
      ),
    },
    {
      key: 'treez_employee_name',
      label: 'Employee Name',
      sortable: true,
      renderCell: (row) =>
        row.treez_employee_name ? (
          <div className="capitalize">{row.treez_employee_name}</div>
        ) : (
          <NaWithTooltip tooltip={NA_EMPLOYEE_TOOLTIP} />
        ),
    },
    {
      key: 'treez_driver_name',
      label: 'Driver Name',
      sortable: true,
      renderCell: (row) =>
        row.treez_driver_name ? (
          <div className="capitalize">{row.treez_driver_name}</div>
        ) : (
          <NaWithTooltip tooltip={NA_EMPLOYEE_TOOLTIP} />
        ),
    },
    {
      key: 'payment_type',
      label: 'Payment Type',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">
          {row.payment_type === 'ATM' ? 'CATM' : row.payment_type}
        </div>
      ),
    }
  );

  return columns;
};
